/**
 * Data-class and status chips.
 *
 * Each chip pairs a glyph with a text label so the distinction between
 * measured, modelled, estimated, synthetic and unavailable never depends on
 * colour alone (PRD 10.3).
 */

import type { DataClass, FeatureValue, Freshness } from '../lib/types'
import { DATA_CLASS_LABEL, FRESHNESS_LABEL, percent, provenanceLine } from '../lib/format'

const DATA_CLASS_GLYPH: Record<DataClass, string> = {
  measured: '●',
  modelled: '◆',
  estimated: '◇',
  synthetic: '▲',
  unavailable: '○',
}

const FRESHNESS_GLYPH: Record<Freshness, string> = {
  fresh: '↑',
  aging: '→',
  stale: '↓',
  missing: '—',
}

export function DataClassChip({ value, title }: { value: DataClass; title?: string }) {
  return (
    <span className={`chip ${value}`} title={title ?? DATA_CLASS_LABEL[value]}>
      <span className="glyph" aria-hidden>
        {DATA_CLASS_GLYPH[value]}
      </span>
      {DATA_CLASS_LABEL[value]}
    </span>
  )
}

export function FreshnessChip({ value }: { value: Freshness }) {
  const tone =
    value === 'fresh' ? 'ok' : value === 'aging' ? 'warn' : value === 'stale' ? 'danger' : 'neutral'
  return (
    <span className={`chip ${tone}`} title={`Data freshness: ${FRESHNESS_LABEL[value]}`}>
      <span className="glyph" aria-hidden>
        {FRESHNESS_GLYPH[value]}
      </span>
      {FRESHNESS_LABEL[value]}
    </span>
  )
}

export function Chip({
  children,
  tone = 'neutral',
  title,
}: {
  children: React.ReactNode
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'synthetic' | 'modelled'
  title?: string
}) {
  return (
    <span className={`chip ${tone}`} title={title}>
      {children}
    </span>
  )
}

export function ConfidenceChip({ value }: { value: number | null }) {
  if (value === null) return null
  const tone = value >= 0.7 ? 'ok' : value >= 0.4 ? 'warn' : 'danger'
  return (
    <span
      className={`chip ${tone}`}
      title={
        'How much of the scoring model had data for this asset, weighted by how confident each ' +
        'source is. Low confidence means the score rests on thin evidence, not that the site is bad.'
      }
    >
      Confidence {percent(value)}
    </span>
  )
}

/** Provenance chips for one feature: data class plus freshness. */
export function FeatureChips({ feature }: { feature: FeatureValue | null | undefined }) {
  if (!feature) return <DataClassChip value="unavailable" />
  return (
    <span className="row tight">
      <DataClassChip value={feature.data_class} title={provenanceLine(feature)} />
      {feature.data_class !== 'unavailable' && <FreshnessChip value={feature.freshness} />}
    </span>
  )
}

export function AvailabilityChip({
  available,
  bookedDays,
}: {
  available: boolean | null | undefined
  bookedDays?: number
}) {
  if (available === null || available === undefined) {
    return (
      <Chip tone="neutral" title="Availability was not checked for this window.">
        Not checked
      </Chip>
    )
  }
  if (available) {
    return (
      <Chip tone="ok" title="Free for the whole campaign window at the time of the check.">
        Available
      </Chip>
    )
  }
  return (
    <Chip
      tone="danger"
      title={
        bookedDays
          ? `${bookedDays} day(s) of the window are already committed in Captura.`
          : 'Committed to another campaign or blocking.'
      }
    >
      Occupied{bookedDays ? ` · ${bookedDays}d` : ''}
    </Chip>
  )
}
